// Invoice Detail Page

// Displays detailed invoice information with AI extraction, document preview,
// attachments, contract validation, and integrated payment review workflow

// react
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// router
import { useNavigate, useParams } from 'react-router-dom';

// context
import { ContextProject, ContextUsersPermissions } from '../Project/Project.context';
import { ContextBanner } from '../../../App.context';

// mui
import {
	alpha,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	Paper,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AutoAwesome as IconAI,
	AutoFixHigh as IconAiExtracted,
	Cancel as IconReject,
	CheckCircle as IconApprove,
	Description as IconDocument,
	Edit as IconEdit,
	FolderOpen as IconFolder,
	InsertDriveFile as IconFile,
	PictureAsPdf as IconPdf,
	PlaylistAddCheck as IconChecklist,
	Receipt as IconInvoice,
	Warning as IconWarning,
} from '@mui/icons-material';

// data
import { Activity, User, Invoice } from '../../../@types';
import { ContextSpendPlan } from '../ProjectV2/ProjectV2.context';
import { useInvoice } from '../../../hooks/useInvoices';
import { useContract } from '../../../hooks/useV2Contracts';

// design system
import { typography } from '../../../utils/designSystem';

// dialogs
import { DialogAddInvoice } from '../../dialogs/DialogAddInvoice/DialogAddInvoice';
import {
	DialogDocumentViewer,
	ViewerDocumentInfo,
} from '../../dialogs/DialogDocumentViewer/DialogDocumentViewer';
import { DialogPrepareForPayment } from '../../dialogs/DialogPrepareForPayment/DialogPrepareForPayment';

// contract utils
import {
	getContractCommitmentAmount,
	getContractNum,
} from '../ProjectV2/Contracts/Contracts.utils';

// v2 components
import { DialogAuditTimeline } from '../../dialogs/DialogAuditTimeline/DialogAuditTimeline';
import { DialogEntityDocuments } from '../../dialogs/DialogEntityDocuments/DialogEntityDocuments';
import { HeaderDetailPage } from '../../ui/HeaderDetailPage/HeaderDetailPage';

// ui
import { BtnSplit } from '../../ui/BtnSplit/BtnSplit';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';
import { resolveContractFxRate, restateAsLocal, type FxRate } from '../../../utils/fxRate';

// sse
import { useV2StatusListener } from '../../../utils/sse/useV2StatusListener';

// data
import { ContractDocument } from '../../../data';

// ai services
import { fetchAiBlob, fetchAiDocuments } from '../../../services/aiApi';
import type { AiDocument } from '../ProjectV2/Documents/documentTypes';

// ui
import { AiDisclaimerBanner } from '../../ui/AiDisclaimerBanner/AiDisclaimerBanner';

// Silver Blob — AI Document Insights

interface ExtractedField {
	label: string;
	extracted: string;
	found: boolean;
}

interface DocumentInsights {
	vendorAddress: string | null;
	recipientName: string | null;
	recipientAddress: string | null;
	paymentTerms: string | null;
	billingType: string | null;
	projectName: string | null;
	contractAmount: string | null;
	progress: string | null;
	summary: string | null;
	documentType: string | null;
	confidence: number;
	extractedAt: string | null;
	extractedFields: ExtractedField[];
}

const EMPTY_INSIGHTS: DocumentInsights = {
	vendorAddress: null,
	recipientName: null,
	recipientAddress: null,
	paymentTerms: null,
	billingType: null,
	projectName: null,
	contractAmount: null,
	progress: null,
	summary: null,
	documentType: null,
	confidence: 0,
	extractedAt: null,
	extractedFields: [],
};

const parseSilverBlob = (content: Record<string, unknown>): DocumentInsights => {
	const pages = Array.isArray(content) ? content : [content];
	const page = (pages[0] as Record<string, unknown>) || {};
	const gfe = (page.general_field_extraction as Record<string, unknown>) || {};
	const fv = (page.field_verification as Record<string, unknown>) || {};

	const str = (v: unknown): string | null => {
		if (!v || v === 'Not Found' || v === 'Not Provided') return null;
		const s = String(v).trim();
		return s || null;
	};

	const parseDollar = (v: unknown): number => {
		if (typeof v === 'number') return v;
		if (typeof v !== 'string') return 0;
		const cleaned = v.replace(/[^0-9.]/g, '');
		return parseFloat(cleaned) || 0;
	};

	// --- scalar insights (used by other cards) ---
	const vendorAddress = str(
		gfe['Vendor Address'] || gfe['vendor_address'] || gfe['Supplier Address'] || gfe['Address']
	);
	const recipientName = str(
		gfe['Recipient'] || gfe['Bill To'] || gfe['Client'] || gfe['To'] || gfe['recipient']
	);
	const recipientAddress = str(
		gfe['Recipient Address'] || gfe['Bill To Address'] || gfe['Client Address']
	);
	const paymentTerms = str(
		gfe['Payment Terms'] || gfe['Terms'] || gfe['payment_terms'] || gfe['Net Terms']
	);
	const billingType = str(
		gfe['Contract Billing Type'] || gfe['Billing Type'] || gfe['billing_type']
	);
	const projectName = str(gfe['Project'] || gfe['project']);
	const contractAmountStr = str(gfe['Contract Amount'] || gfe['contract_amount']);
	const progress = str(gfe['Progress %'] || gfe['progress']);
	const summary = str(page.summary);
	const documentType = str(page.document_type || gfe['Document Type']);

	// --- extracted fields (same logic as dialog's parseSilverBlobContent) ---
	const vendorName = str(
		gfe['Vendor'] ||
			gfe['Supplier'] ||
			gfe['vendor'] ||
			gfe['supplier_name'] ||
			fv['supplier_name'] ||
			page.vendor ||
			page.vendor_name
	);
	const totalAmount = parseDollar(
		gfe['Amount Due This Invoice'] ||
			gfe['Total Amount'] ||
			gfe['Invoice Amount'] ||
			gfe['amount'] ||
			fv['amount'] ||
			page.amount ||
			page.total_amount ||
			0
	);
	const rawCurrency = String(gfe['Currency'] || gfe['currency'] || page.currency || '');
	const amountStr = String(
		gfe['Amount Due This Invoice'] || gfe['Total Amount'] || gfe['Contract Amount'] || ''
	);
	const extractedCurrency = rawCurrency || (amountStr.includes('$') ? 'USD' : '');
	const extractedDate = str(
		gfe['Date'] || gfe['Invoice Date'] || gfe['date'] || page.date || page.invoice_date
	);
	const extractedInvNum = str(
		gfe['Invoice Number'] ||
			gfe['Invoice No'] ||
			gfe['invoice_number'] ||
			fv['invoice_number'] ||
			page.invoice_number
	);
	const extractedDueDate = str(
		gfe['Due Date'] || gfe['due_date'] || fv['due_date'] || page.due_date
	);
	const contractAmountNum = parseDollar(gfe['Contract Amount'] || gfe['contract_amount'] || 0);

	const cur = extractedCurrency || 'USD';

	const extractedFields: ExtractedField[] = [
		{ label: 'Supplier Name', extracted: vendorName || '', found: !!vendorName },
		{ label: 'Document Type', extracted: documentType || '', found: !!documentType },
		{ label: 'Billing Type', extracted: billingType || '', found: !!billingType },
		{ label: 'Project', extracted: projectName || '', found: !!projectName },
		{
			label: 'Contract Amount',
			extracted: contractAmountNum ? `${cur} ${contractAmountNum.toLocaleString()}` : '',
			found: !!contractAmountNum,
		},
		{ label: 'Progress', extracted: progress || '', found: !!progress },
		{ label: 'Invoice Number', extracted: extractedInvNum || '', found: !!extractedInvNum },
		{
			label: 'Invoice Amount',
			extracted: totalAmount ? `${cur} ${totalAmount.toLocaleString()}` : '',
			found: !!totalAmount,
		},
		{ label: 'Invoice Date', extracted: extractedDate || '', found: !!extractedDate },
		{ label: 'Currency', extracted: extractedCurrency || '', found: !!extractedCurrency },
		{ label: 'Due Date', extracted: extractedDueDate || '', found: !!extractedDueDate },
		{ label: 'Summary', extracted: summary || '', found: !!summary },
	];

	return {
		vendorAddress,
		recipientName,
		recipientAddress,
		paymentTerms,
		billingType,
		projectName,
		contractAmount: contractAmountStr,
		progress,
		summary,
		documentType,
		confidence: typeof page.confidence === 'number' ? page.confidence : 0,
		extractedAt: str(page.extractedAt || page.extracted_at),
		extractedFields,
	};
};

// AI Insights Sidebar Card

interface AIInsightsCardProps {
	invoice: Invoice;
	insights: DocumentInsights;
}

const AI_CLASSIFICATION_LABELS = [
	'Supplier Name',
	'Document Type',
	'Billing Type',
	'Project',
	'Contract Amount',
	'Progress',
];

const AIInsightsCard = ({ invoice, insights }: AIInsightsCardProps) => {
	const { t } = useTranslation();
	const theme = useTheme();

	const allFields = insights.extractedFields;
	// Supplier name is no longer denormalized on the invoice; use the populated
	// supplier entity when available.
	const supplierName =
		typeof invoice.supplier === 'object' && invoice.supplier ? invoice.supplier.name : '';
	// Enrich fields: for Supplier Name, show contract value if AI didn't find it, but keep warning icon
	const classificationFields = allFields
		.filter((f) => AI_CLASSIFICATION_LABELS.includes(f.label))
		.map((f) => {
			if (f.label === 'Supplier Name' && !f.found && supplierName) {
				return { ...f, extracted: supplierName, inferred: true };
			}
			return { ...f, inferred: false };
		});
	const summaryField = allFields.find((f) => f.label === 'Summary');
	const foundCount = allFields.filter((f) => f.found).length;

	const getTooltip = (field: { label: string; found: boolean; inferred: boolean }) => {
		if (field.inferred) return `Not found by AI — inferred from contract/system data`;
		if (field.found) return `Extracted from document by AI analysis`;
		return `${field.label} could not be determined from the document`;
	};

	return (
		<Stack spacing={2}>
			{/* Shared AI-Assisted Information banner — sits above the AI-extracted
			   invoice fields so authors see the disclaimer before adopting any AI output. */}
			<AiDisclaimerBanner />
			<Card
				elevation={0}
				sx={{
					border: '1px solid',
					borderColor: alpha(theme.palette.primary.main, 0.15),
					borderRadius: 3,
				}}>
				<CardContent sx={{ p: 2 }}>
					<Tooltip title={t('invoice.aiExtractedTooltip')} placement="top">
						<Stack
							direction="row"
							alignItems="center"
							spacing={1}
							sx={{ mb: 1.5, cursor: 'default' }}>
							<IconAI sx={{ color: 'primary.main', fontSize: 18 }} />
							<Typography variant="subtitle2" fontWeight={700}>
								{t('invoice.aiDocumentInsights')}
							</Typography>
							{allFields.length > 0 && (
								<Tooltip
									title={t('invoice.aiFieldsExtracted', {
										found: foundCount,
										total: allFields.length,
									})}
									placement="right">
									<Chip
										label={`${foundCount}/${allFields.length}`}
										size="small"
										color={foundCount === allFields.length ? 'success' : 'warning'}
										sx={{ ml: 'auto' }}
									/>
								</Tooltip>
							)}
						</Stack>
					</Tooltip>

					{classificationFields.length > 0 ? (
						<>
							<Stack spacing={0.75}>
								{classificationFields.map((field) => (
									<Tooltip key={field.label} title={getTooltip(field)} placement="left">
										<Stack
											direction="row"
											alignItems="center"
											spacing={0.75}
											sx={{ cursor: 'default' }}>
											{field.found ? (
												<IconApprove sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
											) : (
												<IconWarning sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }} />
											)}
											<Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
												{field.label}
											</Typography>
											<Typography
												variant="caption"
												fontWeight={600}
												color={field.inferred ? 'text.secondary' : 'text.primary'}
												sx={{
													ml: 'auto',
													textAlign: 'right',
													fontStyle: field.inferred ? 'italic' : 'normal',
												}}
												noWrap>
												{field.found || field.inferred ? field.extracted : '\u2014'}
											</Typography>
										</Stack>
									</Tooltip>
								))}
							</Stack>

							{summaryField?.found && (
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{
										display: 'block',
										mt: 1.5,
										pt: 1.5,
										borderTop: '1px solid',
										borderColor: 'divider',
										fontStyle: 'italic',
										lineHeight: 1.4,
									}}>
									{summaryField.extracted}
								</Typography>
							)}
						</>
					) : (
						<Typography variant="caption" color="text.disabled">
							{t('invoice.noDocumentInsights')}
						</Typography>
					)}
				</CardContent>
			</Card>
		</Stack>
	);
};

// Invoice Preview Card Component

interface InvoicePreviewCardProps {
	invoice: Invoice;
	insights: DocumentInsights;
	invoiceDecision?: 'pending' | 'approved' | 'rejected';
	decisionTimestamp?: Date | null;
	undoLoading?: boolean;
	rejectionReason?: string | null;
	projectUsers?: User[];
	onStartReview?: () => void;
	onReject?: () => void;
	onMoveBackToReview?: () => void;
	onEditFields?: () => void;
}

const InvoicePreviewCard = ({
	invoice,
	insights,
	invoiceDecision = 'pending',
	decisionTimestamp,
	undoLoading = false,
	rejectionReason,
	projectUsers = [],
	onStartReview,
	onReject,
	onMoveBackToReview,
	onEditFields,
}: InvoicePreviewCardProps) => {
	const { t } = useTranslation();
	const theme = useTheme();

	return (
		<Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
			<CardContent sx={{ p: 0 }}>
				{/* Invoice Content */}
				<Box sx={{ p: 3 }}>
					{/* Invoice Header */}
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="flex-start"
						sx={{ mb: 2 }}>
						<Typography variant="h5" color="primary.main" fontWeight={700}>
							{t('invoice.invoiceHeader')}
						</Typography>
						<Stack direction="row" alignItems="center" spacing={1}>
							<Button
								size="small"
								startIcon={<IconEdit />}
								sx={{ fontSize: typography.body.fontSize }}
								onClick={onEditFields}>
								{t('invoice.editFields')}
							</Button>
						</Stack>
					</Stack>

					{/* From / To / Invoice # */}
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="flex-start"
						sx={{ mb: 2 }}>
						<Stack direction="row" spacing={4}>
							<Box>
								<Typography variant="caption" color="text.secondary" fontWeight={600}>
									{t('from') + ':'}
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{typeof invoice.supplier === 'object' && invoice.supplier
										? invoice.supplier.name
										: ''}
								</Typography>
								{insights.vendorAddress && (
									<Typography variant="body2" color="text.secondary">
										{insights.vendorAddress}
									</Typography>
								)}
							</Box>
							{insights.recipientName && (
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>
										To:
									</Typography>
									<Typography variant="body2" fontWeight={600}>
										{insights.recipientName}
									</Typography>
									{insights.recipientAddress && (
										<Typography variant="body2" color="text.secondary">
											{insights.recipientAddress}
										</Typography>
									)}
								</Box>
							)}
						</Stack>
						<Box sx={{ textAlign: 'right' }}>
							<Typography variant="caption" color="text.secondary" fontWeight={600}>
								{t('invoice.invoiceNum')}
							</Typography>
							<Typography variant="body2" fontWeight={600}>
								{invoice.num}
							</Typography>
						</Box>
					</Stack>

					<Divider sx={{ my: 2 }} />

					{/* Dates & Terms */}
					<Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
						<Box>
							<Typography variant="body2" color="text.secondary">
								{t('invoice.date')}{' '}
								{invoice.date ? formatDate({ date: invoice.date, fallback: '—' }) : '—'}
							</Typography>
						</Box>
						<Box sx={{ textAlign: 'right' }}>
							{insights.paymentTerms && (
								<Typography variant="body2" color="text.secondary">
									{t('invoice.terms')} <strong>{insights.paymentTerms}</strong>
								</Typography>
							)}
							<Typography variant="body2" color="text.secondary">
								{t('invoice.due')}{' '}
								{invoice.dateDue ? formatDate({ date: invoice.dateDue, fallback: '—' }) : '—'}
							</Typography>
						</Box>
					</Stack>

					{/* Line Items Table */}
					<Box sx={{ mb: 3 }}>
						<Typography
							variant="caption"
							color="text.secondary"
							fontWeight={700}
							sx={{ mb: 1, display: 'block' }}>
							{t('invoice.invoiceDetails')}
						</Typography>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell
										sx={{
											fontWeight: 600,
											fontSize: typography.body.fontSize,
											color: 'text.secondary',
										}}>
										{t('invoice.item')}
									</TableCell>
									<TableCell
										align="center"
										sx={{
											fontWeight: 600,
											fontSize: typography.body.fontSize,
											color: 'text.secondary',
										}}>
										{t('invoice.qty')}
									</TableCell>
									<TableCell
										align="right"
										sx={{
											fontWeight: 600,
											fontSize: typography.body.fontSize,
											color: 'text.secondary',
										}}>
										{t('invoice.unitPrice')}
									</TableCell>
									<TableCell
										align="right"
										sx={{
											fontWeight: 600,
											fontSize: typography.body.fontSize,
											color: 'text.secondary',
										}}>
										{t('total')}
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{invoice.lineItems.map((item, index) => (
									<TableRow key={index}>
										<TableCell sx={{ fontSize: typography.body.fontSize }}>
											{index + 1}. {item.description}
										</TableCell>
										<TableCell align="center" sx={{ fontSize: typography.body.fontSize }}>
											{item.quantity}
										</TableCell>
										<TableCell align="right" sx={{ fontSize: typography.body.fontSize }}>
											{formatCurrency({ value: item.unitPrice, currency: invoice.currency })}
										</TableCell>
										<TableCell
											align="right"
											sx={{ fontSize: typography.body.fontSize, fontWeight: 600 }}>
											{formatCurrency({ value: item.total, currency: invoice.currency })}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>

					<Divider sx={{ my: 2 }} />

					{/* Total */}
					<Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={4}>
						<Typography variant="body1" fontWeight={600}>
							Total:
						</Typography>
						<Typography variant="h5" fontWeight={700} color="primary.main">
							{formatCurrency({ value: invoice.amount ?? 0, currency: invoice.currency })}
						</Typography>
					</Stack>
				</Box>

				{/* Actions */}
				<Box
					sx={{
						p: 2,
						borderTop: '1px solid',
						borderColor: 'divider',
						bgcolor: alpha(theme.palette.grey[100], 0.5),
					}}>
					{/* Decision Status Banner */}
					{invoiceDecision === 'approved' && (
						<Paper
							elevation={0}
							sx={{
								p: 2,
								mb: 2,
								bgcolor: alpha(theme.palette.success.main, 0.1),
								border: '2px solid',
								borderColor: 'success.main',
								borderRadius: 2,
							}}>
							<Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
								<Stack direction="row" alignItems="center" spacing={1.5}>
									<IconApprove sx={{ color: 'success.main', fontSize: 28 }} />
									<Box>
										<Typography variant="subtitle1" fontWeight={700} color="success.dark">
											Invoice Approved
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{decisionTimestamp?.toLocaleString()}
										</Typography>
									</Box>
								</Stack>
							</Stack>
						</Paper>
					)}

					{invoiceDecision === 'rejected' && (
						<Paper
							elevation={0}
							sx={{
								p: 2,
								mb: 2,
								bgcolor: alpha(theme.palette.error.main, 0.1),
								border: '2px solid',
								borderColor: 'error.main',
								borderRadius: 2,
							}}>
							<Stack spacing={1.5}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={2}
									justifyContent="space-between">
									<Stack direction="row" alignItems="center" spacing={1.5}>
										<IconReject sx={{ color: 'error.main', fontSize: 28 }} />
										<Box>
											<Typography variant="subtitle1" fontWeight={700} color="error.dark">
												Invoice Rejected
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{decisionTimestamp?.toLocaleString()}
											</Typography>
										</Box>
									</Stack>
									<Button
										size="small"
										onClick={onMoveBackToReview}
										color="inherit"
										disabled={undoLoading}>
										Move Back to Review
									</Button>
								</Stack>
								{rejectionReason && (
									<Typography variant="body2" color="error.dark" sx={{ pl: 0.5 }}>
										Reason: {rejectionReason}
									</Typography>
								)}
							</Stack>
						</Paper>
					)}

					{/* Action Buttons - only show if no decision made yet */}
					{invoiceDecision === 'pending' && (
						<Stack direction="row" spacing={2} justifyContent="center">
							<Button
								variant="outlined"
								color="error"
								startIcon={<IconReject />}
								onClick={onReject}
								sx={{ fontWeight: 600, px: 3 }}>
								{t('invoice.rejectInvoice')}
							</Button>
							<Button
								variant="contained"
								color="primary"
								startIcon={<IconChecklist />}
								onClick={onStartReview}
								sx={{ fontWeight: 600, px: 3 }}>
								{t('invoice.preparePayment')}
							</Button>
						</Stack>
					)}
				</Box>
			</CardContent>
		</Card>
	);
};

const InvoicePageSkeleton = () => (
	<Grid container spacing={3}>
		<Grid item xs={12} md={8}>
			<Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
				<CardContent sx={{ p: 3 }}>
					<Stack spacing={2}>
						<Skeleton variant="text" width="28%" height={40} />
						<Skeleton variant="rounded" height={56} />
						<Skeleton variant="rounded" height={210} />
						<Skeleton variant="rounded" height={72} />
					</Stack>
				</CardContent>
			</Card>
		</Grid>
		<Grid item xs={12} md={4}>
			<Stack spacing={2}>
				<Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
					<CardContent sx={{ p: 2 }}>
						<Stack spacing={1}>
							<Skeleton variant="text" width="58%" height={28} />
							<Skeleton variant="rounded" height={96} />
						</Stack>
					</CardContent>
				</Card>
				<Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
					<CardContent sx={{ p: 2 }}>
						<Stack spacing={1}>
							<Skeleton variant="text" width="45%" height={28} />
							<Skeleton variant="rounded" height={126} />
						</Stack>
					</CardContent>
				</Card>
				<Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
					<CardContent sx={{ p: 2 }}>
						<Stack spacing={1}>
							<Skeleton variant="text" width="52%" height={28} />
							<Skeleton variant="rounded" height={140} />
						</Stack>
					</CardContent>
				</Card>
			</Stack>
		</Grid>
	</Grid>
);

// Documents Card — sidebar card backed by AI documents API

interface DocumentsCardProps {
	documents: AiDocument[];
	loading?: boolean;
	onViewDocument?: (doc: AiDocument) => void;
	onViewAll?: () => void;
}

const DocumentsCard = ({ documents, loading, onViewDocument, onViewAll }: DocumentsCardProps) => {
	const { t } = useTranslation();
	const theme = useTheme();

	const getFileIcon = (filename: string) => {
		const ext = filename.split('.').pop()?.toLowerCase();
		switch (ext) {
			case 'pdf':
				return <IconPdf sx={{ color: theme.palette.error.main, fontSize: 18 }} />;
			case 'doc':
			case 'docx':
				return <IconDocument sx={{ color: theme.palette.primary.main, fontSize: 18 }} />;
			case 'xlsx':
			case 'xls':
				return <IconFile sx={{ color: theme.palette.success.dark, fontSize: 18 }} />;
			default:
				return <IconFile sx={{ fontSize: 18 }} />;
		}
	};

	const formatSize = (bytes: number | string | undefined): string => {
		const n = Number(bytes);
		if (!n || n <= 0) return '';
		const units = ['B', 'KB', 'MB', 'GB'];
		const exp = Math.floor(Math.log(n) / Math.log(1024));
		return `${parseFloat((n / Math.pow(1024, exp)).toFixed(1))} ${units[exp]}`;
	};

	return (
		<Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
			<CardContent sx={{ p: 2 }}>
				{/* Header */}
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
					<Stack direction="row" alignItems="center" spacing={1}>
						<IconFolder sx={{ color: 'text.secondary', fontSize: 18 }} />
						<Typography variant="subtitle2" fontWeight={700}>
							{t('documents')}
						</Typography>
					</Stack>
					<Typography variant="caption" color="text.secondary">
						{loading ? '…' : t('invoice.fileCount', { count: documents.length })}
					</Typography>
				</Stack>

				{/* Document List */}
				{loading ? (
					<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
						Loading…
					</Typography>
				) : documents.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
						{t('invoice.noDocuments')}
					</Typography>
				) : (
					<Stack spacing={0.75} sx={{ mb: 1 }}>
						{documents.map((doc) => (
							<Box
								key={doc.id}
								onClick={() => onViewDocument?.(doc)}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									p: 1,
									borderRadius: 1.5,
									cursor: 'pointer',
									transition: 'all 0.2s',
									'&:hover': {
										bgcolor: alpha(theme.palette.primary.main, 0.04),
									},
								}}>
								{getFileIcon(doc.filename)}
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography variant="body2" fontWeight={500} noWrap>
										{doc.filename}
									</Typography>
								</Box>
								<Typography variant="caption" color="text.secondary">
									{formatSize(
										(doc.metadata as Record<string, unknown>)?.file_size_bytes as
											| string
											| number
											| undefined
									)}
								</Typography>
							</Box>
						))}
					</Stack>
				)}

				{/* View All button */}
				{onViewAll && (
					<Button
						size="small"
						fullWidth
						onClick={onViewAll}
						sx={{ textTransform: 'none', fontWeight: 600 }}>
						{t('invoice.viewAllDocuments')}
					</Button>
				)}
			</CardContent>
		</Card>
	);
};

// Contract & Milestone Card — sidebar card showing linked contract + milestone

interface ContractMilestoneCardProps {
	contractLabel: string;
	contractStatus: string;
	onNavigateContract: () => void;
	milestone: {
		name: string;
		status: string;
		paymentPercentage: number | null;
		paymentAmount: number | null;
		sourceCondition: boolean;
	} | null;
	contractAmount: number;
	currency: string;
	/** Project local currency code; enables the local-equivalent captions. */
	localCurrency?: string;
	/** Resolved contract FX rate (canonical). Drives the local-equivalent caption. */
	fx?: FxRate | null;
	onNavigateMilestone: () => void;
}

const getConditionStatusConfig = (
	t: (k: string) => string
): Record<string, { label: string; color: string }> => ({
	PENDING: { label: t('pending'), color: 'warning' },
	SUBMITTED: { label: t('submitted'), color: 'info' },
	VERIFIED: { label: t('verified'), color: 'success' },
	APPROVED: { label: t('approved'), color: 'success' },
	REJECTED: { label: t('rejected'), color: 'error' },
});

const getContractStatusLabels = (
	t: (k: string) => string
): Record<string, { label: string; color: string }> => ({
	DRAFT: { label: t('draft'), color: 'default' },
	ACTIVE: { label: t('active'), color: 'success' },
	PENDING: { label: t('pending'), color: 'warning' },
	COMPLETED: { label: t('completed'), color: 'success' },
	TERMINATED: { label: t('terminated'), color: 'error' },
	SUSPENDED: { label: t('suspended'), color: 'warning' },
});

const ContractMilestoneCard = ({
	contractLabel,
	contractStatus,
	onNavigateContract,
	milestone,
	contractAmount,
	currency,
	localCurrency,
	fx,
	onNavigateMilestone,
}: ContractMilestoneCardProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const contractCfg = getContractStatusLabels(t)[contractStatus] || {
		label: contractStatus,
		color: 'default',
	};

	// Local equivalent of a reporting figure via the canonical contract FX rate.
	const localEquivalent = (reportingValue: number): string | undefined => {
		const local = restateAsLocal(reportingValue, fx ?? null);
		if (local == null || !localCurrency) return undefined;
		return formatCurrency({ currency: localCurrency, value: local });
	};

	const milestoneAmount = milestone
		? milestone.paymentAmount ||
			(milestone.paymentPercentage && contractAmount
				? (contractAmount * milestone.paymentPercentage) / 100
				: null)
		: null;
	const milestoneCfg = milestone
		? getConditionStatusConfig(t)[milestone.status] || { label: milestone.status, color: 'default' }
		: null;

	return (
		<Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
			<CardContent sx={{ p: 2 }}>
				<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
					<IconChecklist sx={{ color: 'primary.main', fontSize: 18 }} />
					<Typography variant="subtitle2" fontWeight={700}>
						{t('invoice.contractMilestone')}
					</Typography>
				</Stack>

				<Stack spacing={0.75}>
					{/* Contract row */}
					<Paper
						elevation={0}
						onClick={onNavigateContract}
						sx={{
							p: 1.5,
							cursor: 'pointer',
							border: '1px solid',
							borderColor: 'divider',
							borderRadius: 1.5,
							'&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.06) },
						}}>
						<Stack direction="row" alignItems="center" justifyContent="space-between">
							<Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
								<Typography variant="caption" fontWeight={600} noWrap>
									{contractLabel}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{contractAmount ? formatCurrency({ value: contractAmount, currency }) : ''}
								</Typography>
								{contractAmount && localEquivalent(contractAmount) && (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ fontSize: '0.8125rem' }}>
										{localEquivalent(contractAmount)}
									</Typography>
								)}
							</Stack>
							<Chip
								label={contractCfg.label}
								size="small"
								color={
									contractCfg.color as
										| 'default'
										| 'primary'
										| 'secondary'
										| 'error'
										| 'info'
										| 'success'
										| 'warning'
								}
								variant="outlined"
							/>
						</Stack>
					</Paper>

					{/* Milestone row */}
					{milestone && milestoneCfg && (
						<Paper
							elevation={0}
							onClick={onNavigateMilestone}
							sx={{
								p: 1.5,
								cursor: 'pointer',
								border: '2px solid',
								borderColor: 'primary.main',
								bgcolor: alpha(theme.palette.primary.main, 0.06),
								borderRadius: 1.5,
								'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
							}}>
							<Stack direction="row" alignItems="center" justifyContent="space-between">
								<Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
									<Stack direction="row" alignItems="center" spacing={0.5}>
										<Typography variant="caption" fontWeight={600} noWrap>
											{milestone.name}
										</Typography>
										{milestone.sourceCondition && (
											<Tooltip
												title="Additional requirement (based on activity classification)"
												placement="top">
												<IconAiExtracted sx={{ fontSize: 12, color: 'text.disabled' }} />
											</Tooltip>
										)}
									</Stack>
									<Typography variant="caption" color="text.secondary">
										{milestone.paymentPercentage ? `${milestone.paymentPercentage}%` : ''}
										{milestone.paymentPercentage && milestoneAmount ? ' · ' : ''}
										{milestoneAmount ? formatCurrency({ value: milestoneAmount, currency }) : ''}
									</Typography>
									{milestoneAmount && localEquivalent(milestoneAmount) && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ fontSize: '0.8125rem' }}>
											{localEquivalent(milestoneAmount)}
										</Typography>
									)}
								</Stack>
								<Chip
									label={milestoneCfg.label}
									size="small"
									color={
										milestoneCfg.color as
											| 'default'
											| 'primary'
											| 'secondary'
											| 'error'
											| 'info'
											| 'success'
											| 'warning'
									}
									variant="outlined"
								/>
							</Stack>
						</Paper>
					)}
				</Stack>
			</CardContent>
		</Card>
	);
};

// Main Page Component

export const PageInvoice = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { invoiceId } = useParams<{ invoiceId: string }>();

	// context
	const { project } = useContext(ContextProject);
	const { usersPermissions: projectUsers } = useContext(ContextUsersPermissions);
	const { setBanner } = useContext(ContextBanner);
	const { conditions: allConditions, contracts, vouchers } = useContext(ContextSpendPlan);

	// Invoice decision state
	const [invoiceDecision, setInvoiceDecision] = useState<'pending' | 'approved' | 'rejected'>(
		'pending'
	);
	const [decisionTimestamp, setDecisionTimestamp] = useState<Date | null>(null);

	// Prepare for Payment dialog state
	const [preparePaymentOpen, setPreparePaymentOpen] = useState(false);
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [rejectionReason, setRejectionReason] = useState('');
	const [isRejecting, setIsRejecting] = useState(false);
	const [isResettingDecision, setIsResettingDecision] = useState(false);
	const [auditTimelineOpen, setAuditTimelineOpen] = useState(false);
	const [auditActivities, setAuditActivities] = useState<Activity[]>([]);
	const [auditLoading, setAuditLoading] = useState(false);
	const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);

	// Get invoice data from API
	const numericId = Number(invoiceId);
	const {
		data: invoice,
		loading: invoiceLoading,
		error: invoiceError,
		update: updateInvoice,
		refetch: refetchInvoice,
	} = useInvoice({
		id: Number.isFinite(numericId) ? numericId : 0,
		// Populate the supplier entity so the invoice "From" block can show its
		// name; the endpoint otherwise returns `supplier` as a bare id.
		query: { populate: 'supplier' },
	});

	// Live updates: refetch this invoice when someone else changes its status.
	useV2StatusListener({ resource: 'invoice', id: numericId, onChange: refetchInvoice });

	// Look up payment route from linked voucher
	const linkedPaymentRoute = useMemo(() => {
		if (!invoice?.payment || !vouchers.length) return null;
		const v = vouchers.find((v) => v.id === invoice.payment);
		return v?.paymentRoute ?? null;
	}, [invoice?.payment, vouchers]);

	// Edit dialog state
	const [editDialogOpen, setEditDialogOpen] = useState(false);

	// Document viewer state (single-doc, backed by AI documents)
	const [viewerDoc, setViewerDoc] = useState<ContractDocument | null>(null);
	const [viewerBlobFilename, setViewerBlobFilename] = useState('');
	const [viewerDocInfo, setViewerDocInfo] = useState<ViewerDocumentInfo | null>(null);
	const [viewerOpen, setViewerOpen] = useState(false);

	// AI documents for sidebar card
	const [aiDocuments, setAiDocuments] = useState<AiDocument[]>([]);
	const [aiDocsLoading, setAiDocsLoading] = useState(false);

	// The invoice's contract id. The invoice endpoint returns `contract` as a
	// bare id (it does not populate the relation), so resolve the full object
	// ourselves.
	const invoiceContractId =
		typeof invoice?.contract === 'object' && invoice.contract
			? invoice.contract.id
			: typeof invoice?.contract === 'number'
				? invoice.contract
				: null;

	// `contracts` from ContextSpendPlan is paginated, so the invoice's contract
	// may not be on the loaded page. Fetch it directly as a fallback so the
	// Prepare for Payment dialog always has a contract to mount against.
	const contractFromContext = invoiceContractId
		? contracts.find((c) => c.id === invoiceContractId) || null
		: null;

	const { data: fetchedContract } = useContract({
		id: invoiceContractId ?? 0,
		isEnabled: !!invoiceContractId && !contractFromContext,
		query: { populate: 'activity,supplier' },
	});

	// Derive contract data for the edit dialog
	const editContract = useMemo(() => {
		if (!invoice) return null;
		if (typeof invoice.contract === 'object' && invoice.contract) return invoice.contract;
		return contractFromContext || fetchedContract || null;
	}, [invoice, contractFromContext, fetchedContract]);

	const editMilestones = useMemo(() => {
		if (!editContract) return [];
		return allConditions
			.filter((c) => {
				const condContract = typeof c.contract === 'number' ? c.contract : c.contract?.id;
				return condContract === editContract.id && c.conditionType === 'payment';
			})
			.map((c) => ({
				id: c.id,
				name: c.name || `Milestone ${c.sequenceOrder || c.id}`,
				percentage: c.paymentPercentage || 0,
				amount:
					c.paymentAmount ||
					(c.paymentPercentage
						? ((getContractCommitmentAmount(editContract, null) ?? 0) * c.paymentPercentage) / 100
						: 0),
			}));
	}, [allConditions, editContract]);

	// Linked milestone condition — the single milestone this invoice is for.
	// `invoice.milestoneNumber` is the condition's sequenceOrder (not its id),
	// so resolve the actual condition by contract + sequenceOrder. Passing its
	// real id through as linkedConditionId is what lets the Prepare-for-Payment
	// dialog surface this milestone instead of showing no conditions.
	const linkedMilestone = useMemo(() => {
		if (!invoice?.milestoneNumber || !editContract) return null;
		const c = allConditions.find((cond) => {
			const condContract = typeof cond.contract === 'number' ? cond.contract : cond.contract?.id;
			return condContract === editContract.id && cond.sequenceOrder === invoice.milestoneNumber;
		});
		if (!c) return null;
		return {
			id: c.id,
			name: c.name || invoice.milestoneName || `Milestone ${c.sequenceOrder || c.id}`,
			status: c.status,
			paymentPercentage: c.paymentPercentage,
			paymentAmount: c.paymentAmount,
			sourceCondition: !!c.sourceCondition,
		};
	}, [allConditions, editContract, invoice?.milestoneNumber, invoice?.milestoneName]);

	// Fetch AI documents for this invoice (single source of truth)
	const fetchInvoiceDocs = useCallback(async () => {
		if (!invoice) return;
		setAiDocsLoading(true);
		try {
			const [primaryRes, taggedRes] = await Promise.all([
				fetchAiDocuments({
					source_type: 'invoice',
					source_id: invoice.id,
					project_id: project?.id,
					populate: 'uploaded_by',
					page_size: 100,
					order_by: 'uploaded_at',
					order: 'DESC',
				}),
				fetchAiDocuments({
					tags: `invoice:${invoice.id}`,
					project_id: project?.id,
					populate: 'uploaded_by',
					page_size: 100,
					order_by: 'uploaded_at',
					order: 'DESC',
				}),
			]);
			const allDocs = [...(primaryRes.data || []), ...(taggedRes.data || [])];
			const seen = new Set<string>();
			const deduped = allDocs.filter((d) => {
				if (seen.has(d.id)) return false;
				seen.add(d.id);
				return true;
			});
			setAiDocuments(deduped);
		} catch (err) {
			console.error('Failed to fetch invoice AI documents:', err);
		} finally {
			setAiDocsLoading(false);
		}
	}, [invoice, project?.id]);

	useEffect(() => {
		if (invoice) fetchInvoiceDocs();
	}, [invoice, invoice?.id, fetchInvoiceDocs]);

	// Open a single AI document in the viewer
	const handleOpenDocViewer = useCallback(
		(doc: AiDocument) => {
			const metadata = (doc.metadata || {}) as Record<string, unknown>;
			const sizeBytes = Number(metadata.file_size_bytes || 0);
			const sizeStr =
				sizeBytes > 0
					? `${parseFloat((sizeBytes / Math.pow(1024, Math.floor(Math.log(sizeBytes) / Math.log(1024)))).toFixed(1))} ${['B', 'KB', 'MB', 'GB'][Math.floor(Math.log(sizeBytes) / Math.log(1024))]}`
					: '';
			const uploaderName =
				typeof doc.uploaded_by === 'object' && doc.uploaded_by
					? doc.uploaded_by.displayName || doc.uploaded_by.email || ''
					: '';
			setViewerDoc({
				id: doc.id,
				name: doc.filename,
				size: sizeStr,
				type: (doc.filename.split('.').pop()?.toLowerCase() || 'doc') as
					| 'doc'
					| 'pdf'
					| 'image'
					| 'xlsx',
				category: 'other' as const,
				uploadedAt: doc.uploaded_at || '',
				uploadedBy: uploaderName,
			});
			setViewerBlobFilename(doc.filename);
			setViewerDocInfo({
				id: doc.id,
				name: doc.filename,
				jobId: doc.job_id || undefined,
				size: sizeStr || undefined,
				uploadedAt: doc.uploaded_at,
				uploadedBy: uploaderName,
				sourceType: 'invoice',
				sourceLabel:
					typeof invoice?.supplier === 'object' && invoice.supplier ? invoice.supplier.name : '',
			});
			setViewerOpen(true);
		},
		[invoice?.supplier]
	);

	// =========================================================================
	// AI Document Insights — fetch silver blob for rich field data
	// =========================================================================
	const [insights, setInsights] = useState<DocumentInsights>(EMPTY_INSIGHTS);

	useEffect(() => {
		if (!aiDocuments.length) return;
		// Use the first AI document's filename to derive the silver blob path
		const firstDoc = aiDocuments[0];
		const baseName = firstDoc.filename.replace(/\.[^/.]+$/, '');
		const silverBlobPath = `${baseName}-output.json`;

		fetchAiBlob('silver', silverBlobPath)
			.then((blobResponse) => {
				const content =
					typeof blobResponse.content === 'object'
						? (blobResponse.content as Record<string, unknown>)
						: {};
				setInsights(parseSilverBlob(content));
			})
			.catch(() => {
				// Silver blob not available — keep empty insights
			});
	}, [aiDocuments]);

	useEffect(() => {
		if (!invoice) return;

		if (invoice.status === 'REJECTED') {
			setInvoiceDecision('rejected');
			setDecisionTimestamp(invoice.modified ? new Date(invoice.modified) : null);
			setRejectionReason('');
			return;
		}

		if (['READY_FOR_PAYMENT', 'PAYMENT_IN_PROGRESS', 'PAID'].includes(invoice.status)) {
			setInvoiceDecision('approved');
			setDecisionTimestamp(invoice.modified ? new Date(invoice.modified) : null);
			setRejectionReason('');
			return;
		}

		setInvoiceDecision('pending');
		setDecisionTimestamp(null);
		setRejectionReason('');
	}, [invoice, invoice?.id, invoice?.status, invoice?.modified]);

	// Fetch audit log activities for this invoice
	const fetchAuditActivities = useCallback(async () => {
		if (!invoice?.id) return;

		setAuditLoading(true);
		try {
			const accessToken = sessionStorage.getItem('accessToken');
			const idToken = sessionStorage.getItem('idToken');
			const res = await fetch(
				`${process.env.REACT_APP_API_URL}/activities?invoice=${invoice.id}&populate=actor&order=DESC&orderBy=created&limit=50&page=1`,
				{
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);

			if (res.ok) {
				const json = await res.json();
				setAuditActivities(json.data || []);
			}
		} catch (err) {
			console.error('Failed to fetch audit activities:', err);
		} finally {
			setAuditLoading(false);
		}
	}, [invoice?.id]);

	// Fetch audit activities when invoice loads or changes
	useEffect(() => {
		fetchAuditActivities();
	}, [fetchAuditActivities]);

	// Handlers
	const handleBack = () => {
		navigate(`/project/${project?.idWbg}/spend-plans/invoices`);
	};

	const handleStartReview = async () => {
		// DRAFT invoices must transition to PENDING_REVIEW before payment can proceed
		if (invoice?.status === 'DRAFT') {
			const res = await updateInvoice({ status: 'PENDING_REVIEW' });
			if (res.error) {
				setBanner({
					message: res.error.message || 'Failed to move invoice to review.',
					severity: 'error',
				});
				return;
			}
			refetchInvoice();
		}
		setPreparePaymentOpen(true);
	};

	const handleReject = () => {
		setRejectDialogOpen(true);
	};

	const handleConfirmReject = async () => {
		if (!invoice) return;
		const reason = rejectionReason.trim();
		if (!reason) return;

		setIsRejecting(true);
		const res = await updateInvoice({ status: 'REJECTED', rejectionReason: reason });
		setIsRejecting(false);

		if (res.error) {
			setBanner({ message: res.error.message, severity: 'error' });
		} else {
			setInvoiceDecision('rejected');
			setDecisionTimestamp(res.data?.modified ? new Date(res.data.modified) : new Date());
			setRejectDialogOpen(false);
			setRejectionReason('');
			fetchAuditActivities();
		}
	};

	const handleMoveBackToReview = async () => {
		if (!invoice || invoice.status !== 'REJECTED') return;

		setIsResettingDecision(true);
		const res = await updateInvoice({
			status: 'PENDING_REVIEW',
			rejectionReason: null,
		});
		setIsResettingDecision(false);

		if (res.error) {
			setBanner({ message: res.error.message, severity: 'error' });
		} else {
			setInvoiceDecision('pending');
			setDecisionTimestamp(null);
			setRejectionReason('');
			fetchAuditActivities();
		}
	};

	if (!Number.isFinite(numericId)) {
		return (
			<Box sx={{ p: 4 }}>
				<Typography>{t('invoice.invalidId')}</Typography>
				<Button onClick={handleBack} sx={{ mt: 2 }}>
					Back to Invoices
				</Button>
			</Box>
		);
	}

	// DRAFT invoices without AI extraction are still in the upload step —
	// redirect to the invoices list where the user can resume via the dialog.
	// DRAFT invoices WITH AI extraction are in verification and can use this page.
	if (invoice?.status === 'DRAFT' && !invoice.aiExtraction) {
		return (
			<Box sx={{ p: 4, textAlign: 'center' }}>
				<Typography variant="h6" gutterBottom>
					This invoice is still a draft
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Draft invoices can be completed from the Invoices list.
				</Typography>
				<Button variant="contained" onClick={handleBack}>
					Go to Invoices
				</Button>
			</Box>
		);
	}

	// Main Render

	return (
		<Box sx={{ minHeight: '100%', bgcolor: 'background.default' }}>
			{/* V2 Header */}
			<HeaderDetailPage
				icon={IconInvoice}
				loading={invoiceLoading && !invoice}
				breadcrumb={{
					label: t('invoices'),
					to: `/project/${project?.idWbg}/spend-plans/invoices`,
				}}
				title={
					invoice
						? t('invoice.invoiceTitle', { num: invoice.num })
						: t('invoice.invoiceTitleDefault')
				}
				status={invoice?.status}
				variant="financial"
				stats={
					invoice
						? [
								{
									label: t('supplier'),
									value:
										typeof invoice.supplier === 'object' && invoice.supplier
											? invoice.supplier.name
											: '',
								},
								{
									label: t('amount'),
									value: formatCurrency({ value: invoice.amount ?? 0, currency: invoice.currency }),
								},
								{
									label: t('dueDate'),
									value: invoice.dateDue ? formatDate({ date: invoice.dateDue }) : '-',
								},
								{
									label: t('contract'),
									value:
										typeof invoice.contract === 'object'
											? invoice.contract?.num || '-'
											: `#${invoice.contract}`,
									...(typeof invoice.contract === 'object' && invoice.contract?.id
										? {
												to: `/project/${project?.idWbg}/spend-plans/contract/${invoice.contract.id}`,
											}
										: {}),
								},
								...(linkedPaymentRoute
									? [
											{
												label: t('invoice.paymentRoute'),
												value: (
													<Chip
														label={
															linkedPaymentRoute === 'DA_REIMBURSEMENT'
																? t('invoice.daReimbursement')
																: linkedPaymentRoute === 'DIRECT_PAYMENT'
																	? 'Direct Payment'
																	: linkedPaymentRoute
														}
														size="small"
														color="info"
														sx={{ fontWeight: 600, height: 28 }}
													/>
												),
											},
										]
									: []),
							]
						: []
				}
				customAction={
					invoice ? (
						<BtnSplit
							actions={[
								{
									children: t('invoice.editInvoice'),
									onClick: () => setEditDialogOpen(true),
									startIcon: <IconEdit />,
								},
								{
									children: t('invoice.viewAuditLog'),
									onClick: () => setAuditTimelineOpen(true),
								},
								{
									children: t('invoice.viewDocuments'),
									onClick: () => setDocumentsDialogOpen(true),
								},
							]}
						/>
					) : undefined
				}
			/>

			{/* Main Content */}
			<Box sx={{ p: 3 }}>
				{invoiceLoading && !invoice ? (
					<InvoicePageSkeleton />
				) : invoice ? (
					<Grid container spacing={3}>
						{/* Main - Invoice Preview */}
						<Grid item xs={12} md={8}>
							<InvoicePreviewCard
								invoice={invoice}
								insights={insights}
								invoiceDecision={invoiceDecision}
								decisionTimestamp={decisionTimestamp}
								undoLoading={isResettingDecision}
								rejectionReason={
									auditActivities.find((a) => a.type === 'INVOICE_REJECTED')?.note ||
									rejectionReason
								}
								projectUsers={projectUsers}
								onStartReview={handleStartReview}
								onReject={handleReject}
								onMoveBackToReview={handleMoveBackToReview}
								onEditFields={() => setEditDialogOpen(true)}
							/>
						</Grid>

						{/* Sidebar */}
						<Grid item xs={12} md={4}>
							<Stack spacing={2}>
								<AIInsightsCard invoice={invoice} insights={insights} />
								{editContract && (
									<ContractMilestoneCard
										contractLabel={getContractNum(editContract)}
										contractStatus={editContract.status}
										onNavigateContract={() =>
											navigate(`/project/${project?.idWbg}/spend-plans/contract/${editContract.id}`)
										}
										milestone={linkedMilestone}
										contractAmount={
											editContract ? (getContractCommitmentAmount(editContract, null) ?? 0) : 0
										}
										currency={editContract.amounts?.[0]?.currency || invoice.currency || 'USD'}
										localCurrency={project?.currencies?.find((c) => c.type === 'LOCAL')?.currency}
										fx={resolveContractFxRate(editContract, null, project)}
										onNavigateMilestone={() =>
											linkedMilestone
												? navigate(
														`/project/${project?.idWbg}/spend-plans/condition/${linkedMilestone.id}`
													)
												: undefined
										}
									/>
								)}
								<DocumentsCard
									documents={aiDocuments}
									loading={aiDocsLoading}
									onViewDocument={handleOpenDocViewer}
									onViewAll={() => setDocumentsDialogOpen(true)}
								/>
							</Stack>
						</Grid>
					</Grid>
				) : (
					<Paper sx={{ p: 3 }}>
						<Typography variant="h6" gutterBottom>
							Invoice not found
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							{invoiceError || 'This invoice could not be loaded.'}
						</Typography>
						<Button onClick={handleBack}>Back to Invoices</Button>
					</Paper>
				)}
			</Box>

			{/* Edit Invoice Dialog */}
			{invoice && editContract && (
				<DialogAddInvoice
					isOpen={editDialogOpen}
					onClose={() => setEditDialogOpen(false)}
					onSuccess={() => {
						setEditDialogOpen(false);
						refetchInvoice();
						fetchAuditActivities();
					}}
					contractId={editContract.id}
					contractNum={getContractNum(editContract)}
					supplierName={
						editContract.entities?.find((e) => e.type === 'SUPPLIER')?.name ||
						(typeof invoice.supplier === 'object' && invoice.supplier
							? invoice.supplier.name
							: '') ||
						'N/A'
					}
					contractCurrency={editContract.amounts?.[0]?.currency || invoice.currency || 'USD'}
					contractAmount={editContract.amounts?.[0]?.amount || 0}
					milestones={editMilestones}
					projectId={project?.id}
					draftInvoice={invoice}
					editMode
				/>
			)}

			{/* Prepare for Payment Dialog (shared with contracts flow) */}
			{editContract && (
				<DialogPrepareForPayment
					contract={editContract}
					isOpen={preparePaymentOpen}
					linkedConditionId={linkedMilestone?.id ?? null}
					onClose={() => setPreparePaymentOpen(false)}
					onSuccess={async () => {
						const res = await updateInvoice({ status: 'READY_FOR_PAYMENT' });
						if (res.error) {
							setBanner({ message: res.error.message, severity: 'error' });
						} else {
							setPreparePaymentOpen(false);
							refetchInvoice();
							fetchAuditActivities();
						}
					}}
				/>
			)}

			<Dialog
				open={rejectDialogOpen}
				onClose={() => {
					if (isRejecting) return;
					setRejectDialogOpen(false);
				}}
				fullWidth
				maxWidth="sm">
				<DialogTitle>{t('invoice.rejectInvoice')}</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
						{t('invoice.rejectBody')}
					</Typography>
					<TextField
						autoFocus
						fullWidth
						multiline
						minRows={3}
						label={t('invoice.rejectionReason')}
						placeholder={t('invoice.rejectionReasonPlaceholder')}
						value={rejectionReason}
						onChange={(e) => setRejectionReason(e.target.value)}
						error={rejectionReason.trim().length === 0}
						helperText={
							rejectionReason.trim().length === 0 ? t('invoice.rejectionReasonRequired') : undefined
						}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							if (isRejecting) return;
							setRejectDialogOpen(false);
						}}
						disabled={isRejecting}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleConfirmReject}
						disabled={isRejecting || rejectionReason.trim().length === 0}>
						{isRejecting ? t('invoice.rejecting') : t('invoice.confirmReject')}
					</Button>
				</DialogActions>
			</Dialog>

			<DialogAuditTimeline
				isOpen={auditTimelineOpen}
				onClose={() => setAuditTimelineOpen(false)}
				title={t('invoice.auditLog')}
				activities={auditActivities}
				isLoading={auditLoading}
			/>

			{invoice && (
				<DialogEntityDocuments
					isOpen={documentsDialogOpen}
					onClose={() => setDocumentsDialogOpen(false)}
					title="Documents"
					sourceType="invoice"
					sourceId={invoice.id}
					projectId={project?.id}
					onViewDocument={(doc) => handleOpenDocViewer(doc)}
				/>
			)}

			{/* Document Viewer Dialog */}
			{viewerDoc && (
				<DialogDocumentViewer
					isOpen={viewerOpen}
					currentIndex={0}
					documents={[viewerDoc]}
					blobFilenames={[viewerBlobFilename]}
					documentInfos={viewerDocInfo ? [viewerDocInfo] : undefined}
					onClose={() => setViewerOpen(false)}
					onNavigate={() => {
						/* single doc, no-op */
					}}
				/>
			)}
		</Box>
	);
};
